import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  nanoid,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { showError, showNotification } from '@vk-hr-tek/core/notifications';
import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { DynamicEntity } from '@app/types';
import {
  GetSideMenuUserSectionResponse,
  SideMenuService,
  UpdateSideMenuUserSectionRequest,
} from '@app/gen/side_menu';

import { getSideMenu } from '../../../layout/slice';
import { UserSettingsState } from '../user-settings.state';

export const getSideMenuUserSection = createAsyncThunk<
  GetSideMenuUserSectionResponse,
  void,
  ThunkExtra
>(
  'dashboard/getSideMenuUserSection',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const sideMenuService = await inject(SideMenuService);

      const result = await sideMenuService.getSideMenuUserSection();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateSideMenuUserSection = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateSideMenuUserSectionRequest>,
  ThunkExtra
>(
  'dashboard/updateSideMenuUserSection',
  async (
    { values, actions },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const sideMenuService = await inject(SideMenuService);

      await sideMenuService.updateSideMenuUserSection({
        body: values,
      });

      actions.resolve(null);
      dispatch(getSideMenu());
      dispatch(showNotification('Навигация в боковой панели успешно изменена'));
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showError(
          'Не удалось изменить навигацию в боковой панели!\nПопробуйте, пожалуйста, еще раз',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const userSettingsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & UserSettingsState
  >,
) => {
  builder.addCase(getSideMenuUserSection.pending, (state) => {
    state.userSection.status = 'loading';
    state.userSection.menuItems = [];
    state.userSection.name = '';
  });
  builder.addCase(getSideMenuUserSection.fulfilled, (state, { payload }) => {
    state.userSection.status = 'complete';
    state.userSection.name = payload.name;
    state.userSection.menuItems = [
      ...payload.children.map((item) => ({ ...item, id: nanoid() })),
    ];
    state.userSection.needRefetch = false;
  });
  builder.addCase(
    getSideMenuUserSection.rejected,
    (state, { payload, error }) => {
      state.userSection.status = 'failed';
      state.userSection.menuItems = [];
      state.userSection.name = '';
      state.userSection.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.userSection.needRefetch = false;
    },
  );
  builder.addCase(updateSideMenuUserSection.pending, (state) => {
    state.userSection.status = 'loading';
  });
  builder.addCase(updateSideMenuUserSection.fulfilled, (state) => {
    state.userSection.status = 'complete';
    state.userSection.needRefetch = true;
  });
  builder.addCase(updateSideMenuUserSection.rejected, (state) => {
    state.userSection.status = 'complete';
    state.userSection.needRefetch = false;
  });
};
